import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { CreditCard, Loader2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CheckoutModal = ({ product, open, onClose }) => {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [loading, setLoading] = useState(false);

  const checkoutPrice = product?.use_discount ? (product.discount_price || product.price) : product?.price;
  const originalPrice = product?.original_price || product?.price;
  const hasDiscount = product?.use_discount || (product?.original_price && product.original_price > product.price);

  const getTypeLabel = (type) => {
    const labels = { course: "Online Course", kit: "Wellness Kit", bundle: "Wellness Bundle" };
    return labels[type] || "Product";
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email) {
      toast.error("Please enter your email address.");
      return;
    }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/checkout/create`, {
        product_id: product.id,
        customer_email: email,
        customer_name: name,
        use_discount: !!product.use_discount,
      });
      const { payfast_url, form_data } = res.data;

      const form = document.createElement("form");
      form.method = "POST";
      form.action = payfast_url;
      form.target = "_blank";
      Object.entries(form_data).forEach(([key, value]) => {
        const input = document.createElement("input");
        input.type = "hidden";
        input.name = key;
        input.value = value;
        form.appendChild(input);
      });
      document.body.appendChild(form);
      form.submit();
      document.body.removeChild(form);
      toast.success("PayFast payment opened in a new tab.");
      onClose();
    } catch (err) {
      toast.error("Checkout failed. Please try again.");
      setLoading(false);
    }
  };

  if (!product) return null;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent
        data-testid="checkout-modal"
        className="sm:max-w-md rounded-2xl border"
        style={{ borderColor: "var(--ll-border)", background: "var(--ll-bg)" }}
      >
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl font-medium" style={{ color: "var(--ll-text)" }}>
            Complete Your Purchase
          </DialogTitle>
          <DialogDescription className="sr-only">Complete your purchase with PayFast</DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-5 pt-2">
          {/* Product Summary */}
          <div className="p-4 rounded-xl" style={{ background: "var(--ll-bg-secondary)" }}>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium" style={{ color: "var(--ll-text)", fontFamily: "'Outfit', sans-serif" }}>
                  {product.name}
                </p>
                <p className="text-xs" style={{ color: "var(--ll-text-muted)", fontFamily: "'Outfit', sans-serif" }}>
                  {getTypeLabel(product.type)}
                </p>
              </div>
              <div className="text-right">
                {hasDiscount && originalPrice > checkoutPrice && (
                  <span
                    className="text-xs line-through block"
                    style={{ color: "var(--ll-text-muted)", fontFamily: "'Outfit', sans-serif" }}
                  >
                    R{originalPrice?.toLocaleString()}
                  </span>
                )}
                <span className="font-serif text-xl font-semibold" style={{ color: "var(--ll-text)" }}>
                  R{checkoutPrice?.toLocaleString()}
                </span>
              </div>
            </div>
            {hasDiscount && originalPrice > checkoutPrice && (
              <div
                className="mt-3 text-xs px-3 py-1.5 rounded-full inline-block"
                style={{ background: "var(--ll-accent)", color: "var(--ll-text)", fontFamily: "'Outfit', sans-serif" }}
              >
                You save R{(originalPrice - checkoutPrice).toLocaleString()}
              </div>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="checkout-name" className="text-sm" style={{ color: "var(--ll-text-secondary)", fontFamily: "'Outfit', sans-serif" }}>
              Full Name
            </Label>
            <Input
              id="checkout-name"
              data-testid="checkout-name-input"
              placeholder="Your full name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="rounded-xl border bg-white/50 focus:bg-white"
              style={{ borderColor: "var(--ll-border)" }}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="checkout-email" className="text-sm" style={{ color: "var(--ll-text-secondary)", fontFamily: "'Outfit', sans-serif" }}>
              Email *
            </Label>
            <Input
              id="checkout-email"
              data-testid="checkout-email-input"
              type="email"
              placeholder="you@example.com"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="rounded-xl border bg-white/50 focus:bg-white"
              style={{ borderColor: "var(--ll-border)" }}
            />
          </div>

          <button
            type="submit"
            data-testid="checkout-submit-btn"
            disabled={loading}
            className="w-full rounded-full px-6 py-3.5 text-sm font-medium transition-all duration-300 hover:-translate-y-0.5 hover:shadow-lg flex items-center justify-center gap-2 disabled:opacity-50"
            style={{ background: "var(--ll-cta)", color: "var(--ll-cta-text)", fontFamily: "'Outfit', sans-serif" }}
          >
            {loading ? <Loader2 size={16} className="animate-spin" /> : <CreditCard size={16} />}
            {loading ? "Redirecting to PayFast..." : `Pay R${checkoutPrice?.toLocaleString()}`}
          </button>

          <div className="flex items-center justify-center gap-2 text-xs" style={{ color: "var(--ll-text-muted)" }}>
            <ShieldCheck size={14} />
            <span style={{ fontFamily: "'Outfit', sans-serif" }}>Secure payment via PayFast</span>
          </div>
          <p className="text-[10px] text-center" style={{ color: "var(--ll-text-muted)", fontFamily: "'Outfit', sans-serif", opacity: 0.7 }}>
            Note: Use a different email than your PayFast merchant account to avoid payment restrictions.
          </p>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default CheckoutModal;
